import React, { useMemo } from "react";
import { Hotel } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { useAdminImpersonation } from "@/hooks/useAdminImpersonation";
import { useHotel } from "@/hooks/useHotel";
import { useLogout } from "@/hooks/useLogout";
import { hotelService } from "@/services/hotel-service";
import type { UserProfile } from "@/services/authService";
import type { ProfileHotelAccess } from "@/types/hotel";
import { Role } from "@/types/user";

interface ActiveHotelLockOverlayProps {
  isLoading: boolean;
  userProfile: UserProfile | null;
}

const isProfileHotelEnabled = (hotel: ProfileHotelAccess): boolean =>
  ((hotel as Partial<Record<"isEnabled" | "isActive", boolean>>).isEnabled ??
    (hotel as Partial<Record<"isEnabled" | "isActive", boolean>>).isActive) !==
  false;

export const ActiveHotelLockOverlay: React.FC<ActiveHotelLockOverlayProps> = ({
  isLoading: isUserLoading,
  userProfile,
}) => {
  const { t } = useTranslation();
  const { handleLogout } = useLogout();
  const { exitImpersonation, isImpersonating } = useAdminImpersonation();
  const role = userProfile?.role;
  const isHotelScopedRole =
    role === Role.HOTEL_ADMIN ||
    role === Role.FRONT_DESK ||
    role === Role.STAFF;
  const { isLoading } = useHotel(isHotelScopedRole);
  const { data: profileHotels = [], isLoading: isLoadingProfileHotels } =
    useQuery({
      queryKey: ["profileHotels"],
      queryFn: () =>
        hotelService.getProfileHotels({
          includeHotelDetailsFallback: false,
        }),
      enabled: isHotelScopedRole,
    });
  const hasActiveProfileHotel = useMemo(
    () => profileHotels.some(isProfileHotelEnabled),
    [profileHotels],
  );
  const signOutLabel = isImpersonating
    ? t("layout.userMenu.exitImpersonation")
    : t("layout.userMenu.signOut");
  const handleAuthAction = isImpersonating ? exitImpersonation : handleLogout;
  const hasSubscriptionIssue =
    userProfile?.access !== null &&
    userProfile?.access !== undefined &&
    !userProfile.access.hasActiveSubscription;
  const shouldShow =
    isHotelScopedRole &&
    !isUserLoading &&
    !isLoading &&
    !isLoadingProfileHotels &&
    !hasActiveProfileHotel &&
    !hasSubscriptionIssue;

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <section
        aria-labelledby="active-hotel-lock-title"
        aria-describedby="active-hotel-lock-description"
        aria-modal="true"
        className="flex w-full max-w-lg flex-col gap-6 rounded-lg border border-border bg-card p-6 text-card-foreground shadow-xl sm:p-8"
        role="dialog"
      >
        <div className="flex flex-col gap-3">
          <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Hotel aria-hidden className="size-6" />
          </div>
          <div className="flex flex-col gap-2">
            <h2
              className="text-xl font-semibold tracking-normal"
              id="active-hotel-lock-title"
            >
              {t("layout.activeHotelLock.title")}
            </h2>
            <p
              className="text-sm leading-6 text-muted-foreground"
              id="active-hotel-lock-description"
            >
              {t("layout.activeHotelLock.description")}
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-fit"
            onClick={() => {
              void handleAuthAction();
            }}
          >
            {signOutLabel}
          </Button>
        </div>
      </section>
    </div>
  );
};
